import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { string, bool } from "prop-types";
import { ActionMenu, ConfirmNotification, TableView1 } from "@/components";
import { useAuth, useTextDomainContext, useUser } from "@/app/providers";
import {
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  CardMedia,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  Link as MuiLink,
  MenuItem,
  Select,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Toolbar,
  Tooltip,
  Typography,
} from "@mui/material";
import type { GridColDef, GridRenderCellParams } from "@mui/x-data-grid";
import { Link, useNavigate } from "react-router";
import {
  useDeleteUserDocument,
  useDocumentTypeArray,
  useEnergyTypeArray,
  useHasPermission,
  useUserDocuments,
} from "@/hooks";
import { formatDateShort } from "@/utils";
import DownloadIcon from "@mui/icons-material/Download";
import ViewListIcon from "@mui/icons-material/ViewList";
import ViewModuleIcon from "@mui/icons-material/ViewModule";
import VisibilityIcon from "@mui/icons-material/Visibility";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import { useSnackbar } from "notistack";

const codesFull = ["documents-full", "everything-full"];

const codesReadonly = ["documents-readonly", "everything-readonly"];

const feature = "common";

type DocumentsProps = {
  title?: string;
  parentPath?: string;
  showView?: boolean;
  showAdd?: boolean;
  showEdit?: boolean;
  showDownload?: boolean;
  showDelete?: boolean;
};

type DocumentOption = {
  value: string;
  label: string;
};

type DocumentRow = {
  id: string | number;
  energyType?: string;
  documentType?: string;
  name?: string;
  description?: string;
  startDate?: string | null;
  endDate?: string | null;
  originalFileName?: string;
};

type DialogParameters = {
  action?: string;
  id?: string | number;
};

type ConfirmResult = {
  result: string;
  parameters?: DialogParameters;
};

export const Documents = (props: DocumentsProps) => {
  const { TextDomainContext } = useTextDomainContext();
  const { gettext } = useContext(TextDomainContext);
  const { user } = useUser();
  const [newEnergyTypeFilter, setNewEnergyTypeFilter] = useState("");
  const { allow: allowReadonly } = useHasPermission(codesReadonly, feature);
  const { allow: allowFull } = useHasPermission(codesFull, feature);

  const currentTeamId = useMemo(() => {
    return user?.currentTeamId || "";
  }, [user?.currentTeamId]);

  const currentSubsidiaryId = useMemo(() => {
    return user?.currentSubsidiaryId || "";
  }, [user?.currentSubsidiaryId]);

  const currentTenantId = useMemo(() => {
    return user?.currentTenantId || "";
  }, [user?.currentTenantId]);

  const energyTypeFilter = useMemo(() => {
    return newEnergyTypeFilter || "all";
  }, [newEnergyTypeFilter]);

  // Build filter variables from user context and energyType filter
  const filterVariables = useMemo(() => {
    // Get values from user context - always default to "all" if null/empty
    const customer = currentTeamId || "all";
    const subsidiary = currentSubsidiaryId || "all";
    let tenant = currentTenantId || "all";

    // If customer has a value (not "all"), force tenant to "all"
    if (customer !== "all") {
      tenant = "all";
    }

    // Always pass all four parameters
    return {
      customer: customer,
      subsidiary: subsidiary,
      tenant: tenant,
      energyType: energyTypeFilter || "all",
    };
  }, [currentSubsidiaryId, currentTeamId, currentTenantId, energyTypeFilter]);

  const { documents, loading, refetch } = useUserDocuments(filterVariables) as {
    documents: DocumentRow[];
    loading: boolean;
    refetch?: () => void;
  };
  const documentTypes = useDocumentTypeArray() as DocumentOption[];
  const energyTypes = useEnergyTypeArray() as DocumentOption[];
  const navigate = useNavigate();
  // If parentPath is provided and has a value, use it with leading slash, otherwise use empty string
  const parentPath = props.parentPath ? `/${props.parentPath}` : "";
  const { enqueueSnackbar } = useSnackbar();
  const { deleteDocument, loading: deleteLoading } = useDeleteUserDocument();
  const { token } = useAuth();

  const [pdfThumbnails, setPdfThumbnails] = useState<Record<string, string>>(
    {},
  );
  const showItems = useMemo(() => {
    return {
      showView: (allowReadonly || allowFull) ?? false,
      showAdd: allowFull ?? false,
      showEdit: allowFull ?? false,
      showDownload: (allowReadonly || allowFull) ?? false,
      showDelete: allowFull ?? false,
    };
  }, [allowFull, allowReadonly]);

  const title = useMemo(() => {
    return props.title || gettext("All Documents");
  }, [gettext, props.title]);

  const [confirmMessage, setConfirmMessage] = useState("");
  const [showConfirm, setShowConfirm] = useState(false);
  const [dialogParameters, setDialogParameters] = useState<DialogParameters>(
    {},
  );
  const [isDeleting, setIsDeleting] = useState(false);

  // Load view mode from localStorage or default to 'list'
  const [viewMode, setViewMode] = useState(() => {
    const savedViewMode = localStorage.getItem("documentsViewMode");
    return savedViewMode || "list";
  });

  const handleViewModeChange = useCallback(
    (event: React.MouseEvent<HTMLElement>, newViewMode: string | null) => {
      void event;
      if (newViewMode !== null) {
        setViewMode(newViewMode);
        // Save to localStorage
        localStorage.setItem("documentsViewMode", newViewMode);
      }
    },
    [],
  );

  // Generate PDF thumbnails when switching to thumbnail view
  useEffect(() => {
    if (viewMode !== "thumbnail" || documents.length === 0) return;

    const apiUrl = import.meta.env.VITE_API_URL;
    let isCancelled = false;

    const generateThumbnail = async (doc: DocumentRow) => {
      try {
        const downloadUrl = `${apiUrl}/v2/reports/download_user_document?document_id=${doc.id}`;
        const headers: Record<string, string> = {};
        if (token) {
          headers.authorization = `Bearer ${token}`;
        }

        const response = await fetch(downloadUrl, {
          method: "GET",
          headers: headers,
          credentials: "include",
        });

        if (!response.ok || isCancelled) return;

        const blob = await response.blob();
        const url = URL.createObjectURL(blob);

        if (!isCancelled) {
          setPdfThumbnails((current) => ({
            ...current,
            [String(doc.id)]: url,
          }));
        } else {
          URL.revokeObjectURL(url as string);
        }
      } catch {
        // Silently fail for individual thumbnails
      }
    };

    // Generate thumbnails only for documents that don't have them yet
    documents.forEach((doc) => {
      if (!pdfThumbnails[String(doc.id)]) {
        generateThumbnail(doc).then();
      }
    });

    return () => {
      isCancelled = true;
    };
  }, [viewMode, documents, token, pdfThumbnails]);

  // Cleanup thumbnails when switching away from thumbnail view
  useEffect(() => {
    return () => {
      if (viewMode !== "thumbnail") {
        setPdfThumbnails((prev) => {
          Object.values(prev).forEach((url) => {
            if (url) URL.revokeObjectURL(url as string);
          });
          return {};
        });
      }
    };
  }, [viewMode]);

  const handleOnClickDeleteLink = useCallback(
    (action: string, id: string | number | undefined) => {
      setDialogParameters({
        action,
        id,
      });
      setShowConfirm(true);
    },
    [],
  );

  const menuClick = useCallback(
    (action: string, row: DocumentRow) => {
      switch (action) {
        case "document-view":
          navigate(`${parentPath}/documents/view/${row.id}`, {
            state: { row },
          });
          break;
        case "document-edit":
          navigate(`${parentPath}/documents/edit/${row.id}`, {
            state: { row },
          });
          break;
        case "document-download":
          {
            const apiUrl = import.meta.env.VITE_API_URL;
            const downloadUrl = `${apiUrl}/v2/reports/download_user_document?document_id=${row.id}`;
            // Open download in new tab
            window.open(downloadUrl, "_blank");
          }
          break;
        case "document-delete":
          setConfirmMessage(gettext("Do you want to delete the document?"));
          handleOnClickDeleteLink(action, row?.id);
          break;
        default:
          break;
      }
    },
    [gettext, handleOnClickDeleteLink, navigate, parentPath],
  );

  const columns = useMemo<GridColDef<DocumentRow>[]>(
    () => [
      // {field: 'id', headerName: gettext('ID'), width: 90},
      {
        field: "energyType",
        headerName: gettext("Energy Type"),
        width: 150,
        valueGetter: (value: string) => {
          const energyType = energyTypes.find((type) => type.value === value);
          return energyType ? energyType.label : value;
        },
      },
      {
        field: "documentType",
        headerName: gettext("Document Type"),
        width: 150,
        valueGetter: (value: string) => {
          const docType = documentTypes.find((type) => type.value === value);
          return docType ? docType.label : value;
        },
      },
      { field: "name", headerName: gettext("Name"), flex: 1, minWidth: 200 },
      {
        field: "description",
        headerName: gettext("Description"),
        flex: 1,
        minWidth: 200,
      },
      {
        field: "startDate",
        headerName: gettext("Start Date"),
        width: 130,
        type: "date",
        valueGetter: (value: string | null) => value && new Date(value),
        valueFormatter: (value: Date | null) => formatDateShort(value),
      },
      {
        field: "endDate",
        headerName: gettext("End Date"),
        width: 130,
        type: "date",
        valueGetter: (value: string | null) => value && new Date(value),
        valueFormatter: (value: Date | null) => formatDateShort(value),
      },
      {
        field: "originalFileName",
        headerName: gettext("File Name"),
        flex: 1,
        minWidth: 200,
        renderCell: (params: GridRenderCellParams<DocumentRow>) => {
          const apiUrl = import.meta.env.VITE_API_URL;
          const downloadUrl = `${apiUrl}/v2/reports/download_user_document?document_id=${params.row.id}`;
          return (
            <MuiLink
              href={downloadUrl}
              download
              target="_blank"
              rel="noopener noreferrer"
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.5,
                textDecoration: "none",
                "&:hover": {
                  textDecoration: "underline",
                },
              }}
            >
              <DownloadIcon fontSize="small" />
              {params.value || gettext("Download")}
            </MuiLink>
          );
        },
      },
      // {field: 'createdBy', headerName: gettext('Created By'), width: 150},
      // {
      //   field: 'createdAt', headerName: gettext('Created At'),
      //   width: 180,
      //   type: "date",
      //   valueGetter: (value) => value && new Date(value),
      //   valueFormatter: (value) => formatDateShort(value),
      // },
      {
        field: "actions",
        type: "actions",
        headerName: gettext("Actions"),
        hideable: false,
        maxWidth: 75,
        align: "right",
        headerAlign: "right",
        // renderCell: (params) => columnFormatter(params),
        renderCell: (params: GridRenderCellParams<DocumentRow>) => (
          <ActionMenu
            menuFor="document"
            row={params.row}
            showItems={showItems}
            menuClick={menuClick}
          />
        ),
      },
    ],
    [gettext, energyTypes, documentTypes, showItems, menuClick],
  );

  const rows = useMemo(() => documents, [documents]);

  const postToolbar = useMemo(
    () => (
      // <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
      <Stack direction="row" spacing={1} sx={{ pl: 1, alignItems: "center" }}>
        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel id="energy-type-filter-label">
            {gettext("Energy Type")}
          </InputLabel>
          <Select
            labelId="energy-type-filter-label"
            id="energy-type-filter"
            value={energyTypeFilter}
            label={gettext("Energy Type")}
            onChange={(e) => setNewEnergyTypeFilter(e.target.value)}
          >
            <MenuItem value="all">{gettext("All")}</MenuItem>
            {energyTypes.map((type) => (
              <MenuItem key={type.value} value={type.value}>
                {type.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <ToggleButtonGroup
          value={viewMode}
          exclusive
          onChange={handleViewModeChange}
          aria-label={gettext("View Mode")}
          size="small"
        >
          <ToggleButton value="list" aria-label={gettext("List View")}>
            <Tooltip title={gettext("List View")}>
              <ViewListIcon />
            </Tooltip>
          </ToggleButton>
          <ToggleButton
            value="thumbnail"
            aria-label={gettext("Thumbnail View")}
          >
            <Tooltip title={gettext("Thumbnail View")}>
              <ViewModuleIcon />
            </Tooltip>
          </ToggleButton>
        </ToggleButtonGroup>

        {showItems.showAdd && (
          <Button
            variant="contained"
            component={Link}
            to={`${parentPath}/documents/add`}
          >
            {gettext("Add")}
          </Button>
        )}
        {/*</Box>*/}
      </Stack>
    ),
    [
      energyTypeFilter,
      energyTypes,
      gettext,
      handleViewModeChange,
      parentPath,
      showItems.showAdd,
      viewMode,
    ],
  );

  const handleGetResult = async (obj: ConfirmResult) => {
    if (obj.result === "yes" && !isDeleting) {
      // Close dialog immediately
      setShowConfirm(false);

      switch (obj?.parameters?.action) {
        case "document-delete":
          setIsDeleting(true);
          try {
            const documentId = obj?.parameters?.id;
            if (documentId == null) {
              throw new Error(gettext("Missing document id"));
            }

            const result = await deleteDocument(String(documentId));

            if (result?.success) {
              enqueueSnackbar(gettext("Document deleted successfully"), {
                variant: "success",
              });
              // Refetch documents to update the list
              if (refetch) {
                refetch();
              }
            } else {
              const errorMessage = Array.isArray(result?.message)
                ? result.message[0]
                : result?.message || gettext("Failed to delete document");
              enqueueSnackbar(errorMessage, {
                variant: "error",
              });
            }
          } catch (err) {
            const msg = err instanceof Error ? err.message : String(err);
            enqueueSnackbar(
              msg || gettext("An error occurred while deleting the document"),
              {
                variant: "error",
              },
            );
          } finally {
            setIsDeleting(false);
          }
          break;
        default:
          break;
      }
    } else if (obj.result === "no") {
      // Close dialog on "no" as well
      setShowConfirm(false);
    }
  };

  const handleCloseConfirm = () => {
    setShowConfirm(false);
  };

  const renderThumbnailView = () => {
    return (
      <Box>
        <Toolbar
          disableGutters
          sx={{
            pt: 3,
            pb: 3,
            minHeight: "auto",
          }}
        >
          <Typography variant="h4" component="h1" sx={{ flexGrow: 1 }}>
            {title}
          </Typography>
          {postToolbar}
        </Toolbar>

        {loading || deleteLoading || isDeleting ? (
          <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
            <Typography>{gettext("Loading...")}</Typography>
          </Box>
        ) : documents.length === 0 ? (
          <Box
            sx={{
              minHeight: 300,
              backgroundColor: "background.paper",
              borderRadius: 1,
              border: "1px dashed",
              borderColor: "divider",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              flexDirection: "column",
              p: 8,
            }}
          >
            <PictureAsPdfIcon
              sx={{ fontSize: 64, color: "text.disabled", mb: 2 }}
            />
            <Typography
              variant="h6"
              gutterBottom
              sx={{ color: "text.secondary" }}
            >
              {gettext("No Documents Found")}
            </Typography>
            <Typography variant="body2" sx={{ color: "text.secondary" }}>
              {gettext("There are no documents matching your criteria.")}
            </Typography>
          </Box>
        ) : (
          <Grid container spacing={3} sx={{ pb: 2 }}>
            {documents.map((doc) => {
              const energyType = energyTypes.find(
                (type) => type.value === doc.energyType,
              );
              const docType = documentTypes.find(
                (type) => type.value === doc.documentType,
              );
              const apiUrl = import.meta.env.VITE_API_URL;
              const downloadUrl = `${apiUrl}/v2/reports/download_user_document?document_id=${doc.id}`;
              const thumbnailUrl = pdfThumbnails[String(doc.id)];

              return (
                <Grid
                  key={doc.id}
                  size={{ xs: 12, sm: 6, md: 4, lg: 3 }}
                  sx={{
                    display: "flex",
                  }}
                >
                  <Card
                    sx={{
                      width: "100%",
                      height: "100%",
                      minHeight: 450,
                      display: "flex",
                      flexDirection: "column",
                      transition: "transform 0.2s, box-shadow 0.2s",
                      "&:hover": {
                        transform: "translateY(-4px)",
                        boxShadow: 4,
                      },
                    }}
                  >
                    {/* PDF Thumbnail Preview */}
                    <CardMedia
                      sx={{
                        height: 220,
                        backgroundColor: "#f5f5f5",
                        position: "relative",
                        overflow: "hidden",
                      }}
                    >
                      {thumbnailUrl ? (
                        <iframe
                          src={`${thumbnailUrl}#page=1&view=FitH&toolbar=0&navpanes=0&scrollbar=0`}
                          style={{
                            width: "100%",
                            height: "100%",
                            border: "none",
                            pointerEvents: "none",
                            transform: "scale(1.2)",
                            transformOrigin: "top center",
                          }}
                          title={doc.name}
                        />
                      ) : (
                        <Box
                          sx={{
                            height: "100%",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            backgroundColor: "primary.light",
                          }}
                        >
                          <PictureAsPdfIcon
                            sx={{ fontSize: 80, color: "primary.contrastText" }}
                          />
                        </Box>
                      )}
                    </CardMedia>

                    {/* Card Content */}
                    <CardContent sx={{ flexGrow: 1, minHeight: 140, pb: 1 }}>
                      <Typography
                        variant="h6"
                        component="div"
                        noWrap
                        title={doc.name}
                        sx={{ mb: 1 }}
                      >
                        {doc.name}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{ mt: 0.5, color: "text.secondary" }}
                      >
                        {energyType?.label || doc.energyType}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{ color: "text.secondary" }}
                      >
                        {docType?.label || doc.documentType}
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{
                          mt: 1,
                          display: "block",
                          color: "text.secondary",
                        }}
                      >
                        {gettext("Start")}:{" "}
                        {doc.startDate
                          ? formatDateShort(new Date(doc.startDate))
                          : "-"}
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{ display: "block", color: "text.secondary" }}
                      >
                        {gettext("End")}:{" "}
                        {doc.endDate
                          ? formatDateShort(new Date(doc.endDate))
                          : "-"}
                      </Typography>
                    </CardContent>

                    {/* Card Actions */}
                    <CardActions
                      sx={{ justifyContent: "flex-start", px: 2, pb: 2, pt: 0 }}
                    >
                      <Stack
                        direction="row"
                        spacing={0.5}
                        sx={{ flexWrap: "wrap" }}
                      >
                        {showItems.showView && (
                          <Tooltip title={gettext("View")}>
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={() => menuClick("document-view", doc)}
                            >
                              <VisibilityIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                        {showItems.showEdit && (
                          <Tooltip title={gettext("Edit")}>
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={() => menuClick("document-edit", doc)}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                        {showItems.showDownload && (
                          <Tooltip title={gettext("Download")}>
                            <IconButton
                              size="small"
                              color="primary"
                              component="a"
                              href={downloadUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              <DownloadIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                        {showItems.showDelete && (
                          <Tooltip title={gettext("Delete")}>
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => menuClick("document-delete", doc)}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Stack>
                    </CardActions>
                  </Card>
                </Grid>
              );
            })}
          </Grid>
        )}
      </Box>
    );
  };

  return (
    <>
      {viewMode === "list" ? (
        <TableView1
          title={title}
          columns={columns}
          rows={rows}
          loading={loading || deleteLoading || isDeleting}
          postToolbar={postToolbar}
          showGridToolbar
          showGridToolbarQuickFilter
        />
      ) : (
        renderThumbnailView()
      )}

      <ConfirmNotification
        isOpen={showConfirm}
        parameters={dialogParameters}
        getResult={handleGetResult}
        message={confirmMessage}
        onClose={handleCloseConfirm}
      />
    </>
  );
};

Documents.propTypes = {
  title: string,
  parentPath: string,
  showView: bool,
  showAdd: bool,
  showEdit: bool,
  showDownload: bool,
  showDelete: bool,
};
